/**
 * Mehrere **Notizen** gemeinsam skalieren — alle um denselben Faktor, von der
 * gegenüberliegenden Ecke des Auswahlrahmens aus.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import {
  getBoundingBox,
  getResizeAlignment,
  movingEdgesFor,
  snapToGrid,
  type AlignmentLines,
  type Bounds,
  type MovingRect,
} from './canvasAlignment'
import type { ResizeCorner, SizeLimits } from './canvasAlignment'

export interface GroupResizeInput {
  /** Die Auswahl, in der Lage VOR dem Ziehen. */
  rects: MovingRect[]
  /** Die angefasste Ecke — die gegenüberliegende ist der Anker. */
  corner: ResizeCorner
  /** Wohin der Zeiger gewandert ist, in Canvas-Koordinaten. */
  pointer: { x: number; y: number }
  /** Kleinste und größte erlaubte Kantenlänge einer einzelnen Notiz. */
  limits: SizeLimits
  /** Rasterweite. 0 schaltet das Rastern ab. */
  grid: number
}

/** Die Ecke des Rahmens, die beim Ziehen stehen bleibt. */
function anchorOf(box: { left: number; right: number; top: number; bottom: number }, corner: ResizeCorner) {
  return {
    x: corner.endsWith('left') ? box.right : box.left,
    y: corner.startsWith('top') ? box.bottom : box.top,
  }
}

/**
 * Die neue Lage und Größe jeder Notiz, in der Reihenfolge der Eingabe.
 *
 * Jede Notiz wird mit demselben Faktor um den Anker gestreckt — Position UND
 * Maße. Dadurch **wachsen die Abstände mit**: die Gruppe bleibt sich ähnlich,
 * statt beim Vergrößern enger zusammenzurücken. Das ist die Lesart von
 * „gleichermaßen" und das, was FigJam und Miro tun.
 *
 * Die Schriftgröße folgt von selbst, sie hängt an Breite und Höhe
 * (`computeStickyFontSize`).
 */
export function getGroupResize({ rects, corner, pointer, limits, grid }: GroupResizeInput): MovingRect[] {
  const box = getBoundingBox(rects)
  if (!box) return []

  const anchor = anchorOf(box, corner)
  const width = box.right - box.left
  const height = box.bottom - box.top
  if (width <= 0 || height <= 0) return rects

  // Die STÄRKERE Achse bestimmt den Faktor. Wer waagerecht auf die doppelte
  // Breite zieht, bekommt Faktor 2 — die Bewegung setzt sich unmittelbar durch.
  //
  // Nicht das Verhältnis der Diagonalen: das ist spürbar träge. Bei einem
  // quadratischen Rahmen ergäbe eine verdoppelte Breite nur Faktor 1,58 — und
  // quadratisch ist bei diesen Notizen der Regelfall.
  const factor = Math.max(
    Math.abs(pointer.x - anchor.x) / width,
    Math.abs(pointer.y - anchor.y) / height,
  )

  // Die Grenzen gelten für die GRUPPE, nicht je Notiz: Sonst bliebe die
  // kleinste an ihrer Untergrenze stehen, während die übrigen weiterschrumpfen
  // — die Anordnung zerfiele.
  //
  // Geklemmt wird gegen RASTERKONFORME Grenzen: 80 wird zu 96 (4 × 24), 640 zu
  // 624 (26 × 24). Sonst überführe das Rastern die Grenze gleich wieder — ein
  // Faktor, der exakt auf 640 klemmt, ergäbe gerundet 648. Die engeren Grenzen
  // sind der Preis dafür, dass beide Regeln zugleich gelten.
  const minAllowed = grid > 0 ? Math.ceil(limits.min / grid) * grid : limits.min
  const maxAllowed = grid > 0 ? Math.floor(limits.max / grid) * grid : limits.max
  const smallest = Math.min(...rects.map(r => Math.min(r.width, r.height)))
  const largest = Math.max(...rects.map(r => Math.max(r.width, r.height)))
  const clamped = Math.min(
    Math.max(factor, minAllowed / smallest),
    maxAllowed / largest,
  )

  // Gerastert wird der FAKTOR, nicht die einzelnen Maße.
  //
  // Breite und Höhe getrennt zu runden zerbricht das Seitenverhältnis, sobald
  // eine Notiz nicht quadratisch ist: Aus 160 × 170 wird bei Faktor 1,1 dann
  // 168 × 192 — höher als breit, obwohl der Faktor für beide Achsen derselbe
  // war. Solche Größen gibt es reichlich, die Vorgabe hat mehrfach gewechselt
  // (200 × 160, 160 × 170, 160 × 166, heute 168 × 168).
  //
  // Stattdessen wird EINE Referenzbreite aufs Raster gelegt und der daraus
  // folgende Faktor auf alle Maße angewandt. Für den Regelfall — alle Notizen
  // gleich groß und rasterkonform — landen damit sämtliche Maße exakt auf dem
  // Raster UND behalten ihr Verhältnis. Bei gemischten Größen gewinnt das
  // Verhältnis; beides zugleich ginge nur bei ganzzahligen Faktoren (168 = 7×24
  // und 192 = 8×24 verlangen, dass 7f und 8f ganzzahlig sind).
  //
  // Referenz ist die breiteste Notiz — die auffälligste, an der man ein
  // krummes Maß am ehesten sähe.
  const reference = rects.reduce((a, b) => (b.width > a.width ? b : a))
  let scale = clamped
  if (grid > 0 && reference.width > 0) {
    const target = Math.max(snapToGrid(reference.width * clamped, grid), grid)
    scale = target / reference.width
    // Das Rastern kann die Grenzen wieder überschreiten — dann eine Stufe
    // zurück, statt sie zu verletzen.
    if (largest * scale > maxAllowed) scale = (target - grid) / reference.width
    if (smallest * scale < minAllowed) scale = (target + grid) / reference.width
  }

  // Die POSITIONEN rasten weiterhin einzeln ein: Sie hängen nicht am
  // Seitenverhältnis, und so liegt jede Notiz auf einem Rasterpunkt.
  //
  // Das gilt auch für die Ankerecke: Liegt sie schon auf dem Raster (der
  // Normalfall), bleibt sie exakt stehen. Liegt sie daneben, rutscht sie auf
  // den nächsten Punkt — „danach liegt alles auf dem Raster" wiegt schwerer
  // als „die nicht angefasste Ecke steht absolut still".
  return rects.map(rect => ({
    x: snapToGrid(anchor.x + (rect.x - anchor.x) * scale, grid),
    y: snapToGrid(anchor.y + (rect.y - anchor.y) * scale, grid),
    width: Math.max(Math.round(rect.width * scale), 1),
    height: Math.max(Math.round(rect.height * scale), 1),
  }))
}

/* ------------------------------------------------------------------------ *
 * Skalieren MIT Ausrichtung
 * ------------------------------------------------------------------------ */

export interface GroupResizeAlignmentInput extends GroupResizeInput {
  /** Die Ausrichtungspartner — alles, was NICHT mitskaliert wird. */
  others: Bounds[]
  /** Fangschwelle in CANVAS-Pixeln (`ALIGNMENT_THRESHOLD / zoom`). */
  threshold: number
}

export interface GroupResizeAlignmentResult extends AlignmentLines {
  rects: MovingRect[]
}

/**
 * Dasselbe wie `getGroupResize`, nur richtet der **Auswahlrahmen** sich dabei
 * an den anderen Elementen aus — genau wie eine einzelne Notiz an ihrer Ecke.
 *
 * Die Übersetzung ist dieselbe wie beim Verschieben, eine Ebene komplizierter:
 * Aus der Zeigerlage folgt der rohe Faktor, daraus das rohe Rahmen-Rechteck.
 * `getResizeAlignment` rastet dessen geführte Kante ein — es geht ohnehin von
 * genau EINEM Freiheitsgrad aus, und der Gruppen-Faktor ist genau das. Aus der
 * eingerasteten Rahmenbreite folgt der endgültige Faktor für alle.
 *
 * Zwei Fälle führen zurück auf `getGroupResize`, das den FAKTOR rastet:
 *
 * 1. Es greift keine Ausrichtung — dann soll das Raster wie bisher gelten.
 * 2. Die Ausrichtung liefe aus den Größengrenzen der GRUPPE heraus. Die
 *    Grenzen gelten für die Gruppe, nicht je Notiz: Sonst bliebe die kleinste
 *    an ihrer Untergrenze stehen, während die übrigen weiterschrumpfen. Und
 *    was nicht hält, gehört auch nicht angezeigt.
 *
 * Rastet der Rahmen ein, liegen die Elemente danach NICHT mehr zwangsläufig
 * auf dem Punktraster: Die fremde Kante gewinnt, die inneren Maße folgen dem
 * Faktor. Bei einer einzelnen Notiz ist das schon heute so — „die Ausrichtung
 * schlägt das Raster" gilt überall.
 */
export function getGroupResizeAlignment({
  rects, corner, pointer, limits, grid, others, threshold,
}: GroupResizeAlignmentInput): GroupResizeAlignmentResult {
  const ohneLinien = (next: MovingRect[]): GroupResizeAlignmentResult =>
    ({ rects: next, vertical: [], horizontal: [], measures: [] })

  const box = getBoundingBox(rects)
  if (!box) return ohneLinien([])

  const width = box.right - box.left
  const height = box.bottom - box.top
  if (width <= 0 || height <= 0) return ohneLinien(rects)

  const anchor = anchorOf(box, corner)
  const moving = movingEdgesFor(corner)
  const factor = Math.max(
    Math.abs(pointer.x - anchor.x) / width,
    Math.abs(pointer.y - anchor.y) / height,
  )

  const aligned = getResizeAlignment(
    {
      width: width * factor,
      height: height * factor,
      x: moving.x === 'left' ? anchor.x - width * factor : anchor.x,
      y: moving.y === 'top' ? anchor.y - height * factor : anchor.y,
    },
    moving,
    others,
    threshold,
    // Weder Raster noch Grenzen: Beide gelten für die GRUPPE und werden hier
    // unten am Faktor entschieden. Der Rahmen ist kein Element, seine Maße
    // müssen weder rastern noch in die Größengrenzen einer Notiz passen.
    0,
    { min: 0, max: Infinity },
  )

  const scale = aligned.rect.width / width
  const smallest = Math.min(...rects.map(r => Math.min(r.width, r.height)))
  const largest = Math.max(...rects.map(r => Math.max(r.width, r.height)))
  if (!aligned.snapped || scale < limits.min / smallest || scale > limits.max / largest) {
    return ohneLinien(getGroupResize({ rects, corner, pointer, limits, grid }))
  }

  return {
    // Die Positionen rastern hier NICHT: Sie folgen dem Faktor, sonst risse das
    // Runden die eingerastete Kante wieder von ihrem Ziel weg.
    rects: rects.map(rect => ({
      x: anchor.x + (rect.x - anchor.x) * scale,
      y: anchor.y + (rect.y - anchor.y) * scale,
      width: Math.max(Math.round(rect.width * scale), 1),
      height: Math.max(Math.round(rect.height * scale), 1),
    })),
    vertical: aligned.vertical,
    horizontal: aligned.horizontal,
    measures: aligned.measures,
  }
}
